import {
  Coding,
  Medication,
  MedicationAdministration,
  MedicationDispense,
  MedicationRequest,
  MedicationStatement,
} from "@medplum/fhirtypes";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
// Import only what we need from series-utils
import {
  MedicationSeries,
  getMedicationCode,
  getMedicationCoding,
} from "../../table-transforms/series-utils";
import { MedicationWithRefs } from "../../table-transforms/medication-types";
import { MEDICATION_RESOURCE_TYPE } from "../../resource-configs/medication-columns";

export type MedicationSeriesRowData = {
  id: string;
  medication: string;
  dose: string;
  quantity: string;
  code: string;
  coding?: Coding;
  lastFillDate: string;
  date: string;
  originalData: MedicationSeries | EnhancedMedicationSeries;
};

// Keep the old type for backward compatibility
export type MedicationRowData = MedicationSeriesRowData;

export function getMedications(bundle: FhirBundleSdk): MedicationWithRefs[] {
  const medicationsWithRefs: MedicationWithRefs[] = [];

  const medications = bundle.getMedications();
  const medicationAdministration = bundle.getMedicationAdministrations();
  const medicationDispense = bundle.getMedicationDispenses();
  const medicationStatement = bundle.getMedicationStatements();
  const medicationRequest = bundle.getMedicationRequests();

  for (const medication of medications) {
    const medicationWithRefs = getMedicationWithRefs(
      medication,
      medicationAdministration,
      medicationDispense,
      medicationStatement,
      medicationRequest
    );

    medicationsWithRefs.push(medicationWithRefs);
  }

  return medicationsWithRefs;
}

export function getMedicationWithRefs(
  medication: Medication,
  medicationAdministrations: MedicationAdministration[],
  medicationDispenses: MedicationDispense[],
  medicationStatements: MedicationStatement[],
  medicationRequests: MedicationRequest[]
): MedicationWithRefs {
  const medicationWithRef: MedicationWithRefs = {
    medication,
    administration: [],
    dispense: [],
    statement: [],
    requests: [],
  };

  for (const medicationTypes of [
    ...medicationAdministrations,
    ...medicationDispenses,
    ...medicationStatements,
    ...medicationRequests,
  ]) {
    const medRefId = getMedicationReferenceId(medicationTypes) || "";

    if (medRefId === medication.id) {
      if (medicationTypes.resourceType === "MedicationAdministration") {
        medicationWithRef.administration.push(medicationTypes);
      } else if (medicationTypes.resourceType === "MedicationDispense") {
        medicationWithRef.dispense.push(medicationTypes);
      } else if (medicationTypes.resourceType === "MedicationStatement") {
        medicationWithRef.statement.push(medicationTypes);
      } else if (medicationTypes.resourceType === "MedicationRequest") {
        medicationWithRef.requests.push(medicationTypes);
      }
    }
  }

  return medicationWithRef;
}

export function getMedicationReferenceId(
  medication:
    | MedicationAdministration
    | MedicationDispense
    | MedicationStatement
    | MedicationRequest
): string | undefined {
  if (medication.medicationReference?.reference) {
    return medication.medicationReference.reference.split("/")[1];
  }

  return undefined;
}

// Custom medication series type that includes individual events
export type MedicationEvent = {
  type: "administration" | "dispense" | "request" | "statement";
  date: string;
  resource: MedicationAdministration | MedicationDispense | MedicationRequest | MedicationStatement;
  medicationWithRefs: MedicationWithRefs;
};

export type EnhancedMedicationSeries = {
  id: string;
  code: string;
  codeDisplay: string;
  resourceType: string;
  events: MedicationEvent[];
  latestDate: string;
  earliestDate: string;
  medicationWithRefs: MedicationWithRefs; // Keep reference to the original grouped data
};

function createMedicationSeries(medications: MedicationWithRefs[]): EnhancedMedicationSeries[] {
  const seriesMap = new Map<string, EnhancedMedicationSeries>();

  for (const medicationWithRefs of medications) {
    const code = getMedicationCode(medicationWithRefs);
    const codeDisplay =
      medicationWithRefs.medication.code?.text ||
      medicationWithRefs.medication.code?.coding?.[0]?.display ||
      "Unknown medication";

    // Collect all events for this medication
    const events: MedicationEvent[] = [];

    // Add administration events
    medicationWithRefs.administration.forEach(admin => {
      const date =
        admin.effectiveDateTime || admin.effectivePeriod?.start || admin.effectivePeriod?.end;
      if (date) {
        events.push({
          type: "administration",
          date,
          resource: admin,
          medicationWithRefs,
        });
      }
    });

    // Add dispense events
    medicationWithRefs.dispense.forEach(dispense => {
      if (dispense.whenHandedOver) {
        events.push({
          type: "dispense",
          date: dispense.whenHandedOver,
          resource: dispense,
          medicationWithRefs,
        });
      }
    });

    // Add request events
    medicationWithRefs.requests.forEach(request => {
      if (request.authoredOn) {
        events.push({
          type: "request",
          date: request.authoredOn,
          resource: request,
          medicationWithRefs,
        });
      }
    });

    // Add statement events
    medicationWithRefs.statement.forEach(statement => {
      const date =
        statement.effectiveDateTime ||
        statement.effectivePeriod?.start ||
        statement.effectivePeriod?.end;
      if (date) {
        events.push({
          type: "statement",
          date,
          resource: statement,
          medicationWithRefs,
        });
      }
    });

    // Sort events by date (even if empty, we still want to show the medication)
    events.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
    const earliestDate = events[0]?.date || "";
    const latestDate = events[events.length - 1]?.date || "";

    if (!seriesMap.has(code)) {
      seriesMap.set(code, {
        id: `series-${code}-${earliestDate}-${latestDate}`,
        code,
        codeDisplay,
        resourceType: MEDICATION_RESOURCE_TYPE,
        events: [],
        latestDate: events[events.length - 1]?.date || "",
        earliestDate: events[0]?.date || "",
        medicationWithRefs,
      });
    }

    const series = seriesMap.get(code);
    if (series) {
      series.events.push(...events);

      // Update date range
      const allDates = series.events.map(e => e.date);
      const sortedAsc = allDates.sort((a, b) => dayjs(a).diff(dayjs(b)));
      const sortedDesc = allDates.sort((a, b) => dayjs(b).diff(dayjs(a)));
      series.earliestDate = sortedAsc[0] || "";
      series.latestDate = sortedDesc[0] || "";
    }
  }

  // Return series sorted by latest date (newest first)
  return Array.from(seriesMap.values()).sort((a, b) => {
    return dayjs(b.latestDate).diff(dayjs(a.latestDate));
  });
}

export function getMedicationRows({ bundle }: { bundle: FhirBundleSdk }): MedicationRowData[] {
  const medications = getMedications(bundle);

  // Create enhanced medication series with individual events
  const medicationSeries = createMedicationSeries(medications);

  // Convert series to row data
  return medicationSeries.map(series => {
    const latestMedication = series.medicationWithRefs;

    return {
      id: `series-${series.code}`,
      medication: latestMedication.medication.code?.text ?? "-",
      dose: getMedicationDose(latestMedication),
      quantity: getMedicationQuantity(latestMedication),
      code: getMedicationCode(latestMedication),
      coding: getMedicationCoding(latestMedication),
      lastFillDate: getLastFillDate(latestMedication),
      date: getStartDate(latestMedication),
      originalData: series, // Now contains the enhanced series with events
    };
  });
}

// formatDate function removed - no longer needed after removing dateRange

export function getMedicationDose(medicationWithRefs: MedicationWithRefs): string {
  const latestAdministered = getLatestAdministered(medicationWithRefs.administration);

  const adminDosage = latestAdministered?.dosage;
  const hasValidDosage = adminDosage?.dose?.value && adminDosage?.dose?.unit;
  const adminInstructions = hasValidDosage
    ? `${adminDosage?.dose?.value} ${adminDosage?.dose?.unit}`
    : undefined;

  return adminInstructions ?? "-";
}

function getMedicationQuantity(medicationWithRefs: MedicationWithRefs): string {
  const latestDispensed = getLatestDispensed(medicationWithRefs.dispense);
  const latestStatement = getLatestStatement(medicationWithRefs.statement);

  const { value: dValue, unit: dUnit } = latestDispensed?.quantity || {};
  const mainQuantity = dValue && dUnit ? `${dValue} ${dUnit}` : undefined;

  const { value: sValue, unit: sUnit } =
    latestStatement?.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity || {};
  const fallbackQuantity = sValue && sUnit ? `${sValue} ${sUnit}` : undefined;

  return mainQuantity ?? fallbackQuantity ?? "-";
}

function getLastFillDate(medicationWithRefs: MedicationWithRefs): string {
  const latestDispensed = getLatestDispensed(medicationWithRefs.dispense);
  const hasValidDate = latestDispensed?.whenHandedOver;
  return hasValidDate ? dayjs(latestDispensed.whenHandedOver).format(ISO_DATE) : "-";
}

// Removed unused function - now using getMedicationCodeForGrouping from series-utils

function getStartDate(medicationWithRefs: MedicationWithRefs): string {
  const latestAdministered = getLatestAdministered(medicationWithRefs.administration);
  const latestDispensed = getLatestDispensed(medicationWithRefs.dispense);
  const latestStatement = getLatestStatement(medicationWithRefs.statement);
  const latestRequest = getLatestRequest(medicationWithRefs.requests);

  const administeredTime =
    latestAdministered?.effectiveDateTime ||
    latestAdministered?.effectivePeriod?.start ||
    latestAdministered?.effectivePeriod?.end;
  const statementTime =
    latestStatement?.effectiveDateTime ||
    latestStatement?.effectivePeriod?.start ||
    latestStatement?.effectivePeriod?.end;
  const dispensedTime = latestDispensed?.whenHandedOver;
  const requestTime = latestRequest?.authoredOn;

  const time = administeredTime || statementTime || dispensedTime || requestTime;

  if (time) {
    return dayjs(time).format(ISO_DATE);
  }

  return "-";
}

export function getLatestAdministered(
  administrations: MedicationAdministration[]
): MedicationAdministration | undefined {
  return administrations.sort((a, b) => {
    const aTime = a.effectiveDateTime || a.effectivePeriod?.start || a.effectivePeriod?.end;
    const bTime = b.effectiveDateTime || b.effectivePeriod?.start || b.effectivePeriod?.end;

    return dayjs(bTime).diff(dayjs(aTime));
  })[0];
}

export function getLatestDispensed(
  dispenses: MedicationDispense[]
): MedicationDispense | undefined {
  return dispenses.sort((a, b) => {
    const aTime = a.whenHandedOver;
    const bTime = b.whenHandedOver;
    return dayjs(bTime).diff(dayjs(aTime));
  })[0];
}

export function getLatestStatement(
  statements: MedicationStatement[]
): MedicationStatement | undefined {
  return statements.sort((a, b) => {
    const aTime = a.effectiveDateTime || a.effectivePeriod?.start || a.effectivePeriod?.end;
    const bTime = b.effectiveDateTime || b.effectivePeriod?.start || b.effectivePeriod?.end;
    return dayjs(bTime).diff(dayjs(aTime));
  })[0];
}

export function getLatestRequest(requests: MedicationRequest[]): MedicationRequest | undefined {
  return requests.sort((a, b) => {
    const aTime = a.authoredOn;
    const bTime = b.authoredOn;
    return dayjs(bTime).diff(dayjs(aTime));
  })[0];
}
